import carDetailingBg from '@/assets/images/polish-car-3.jpeg';
import aircondBg from '@/assets/images/aircond-service.jpeg';
import fabricBg from '@/assets/images/fabric-cleaning.jpeg';
import sanitiseBg from '@/assets/images/sanitise.jpeg';
import pestControlBg from '@/assets/images/pest-control.jpeg';

// Polish Kereta Before and After Images
import carBeforeImg1 from '@/assets/images/car-before-1.jpeg';
import carAfterImg1 from '@/assets/images/car-after-1.jpeg';
import carBeforeImg2 from '@/assets/images/car-before-2.jpeg';
import carAfterImg2 from '@/assets/images/car-after-2.jpeg';
import carBeforeImg3 from '@/assets/images/car-before-3.jpeg';
import carAfterImg3 from '@/assets/images/car-after-3.jpeg';
import carBeforeImg4 from '@/assets/images/car-before-4.jpeg';
import carAfterImg4 from '@/assets/images/car-after-4.jpeg';

// Aircond Service Before and After Images
import aircondBeforeImg1 from '@/assets/images/aircond-before-1.jpeg';
import aircondAfterImg1 from '@/assets/images/aircond-after-1.jpeg';
import aircondBeforeImg2 from '@/assets/images/aircond-before-2.jpeg';
import aircondAfterImg2 from '@/assets/images/aircond-after-2.jpeg';
import aircondBeforeImg3 from '@/assets/images/aircond-before-3.jpeg';
import aircondAfterImg3 from '@/assets/images/aircond-after-3.jpeg';
import aircondBeforeImg4 from '@/assets/images/aircond-before-4.jpeg';
import aircondAfterImg4 from '@/assets/images/aircond-after-4.jpeg';


// Fabric Cleaning Before and After Images
import fabricBeforeImg1 from '@/assets/images/fabric-before-1.jpeg';
import fabricAfterImg1 from '@/assets/images/fabric-after-1.jpeg';
import fabricBeforeImg2 from '@/assets/images/fabric-before-2.jpeg';
import fabricAfterImg2 from '@/assets/images/fabric-after-2.jpeg';
import fabricBeforeImg3 from '@/assets/images/fabric-before-3.jpeg';
import fabricAfterImg3 from '@/assets/images/fabric-after-3.jpeg';
import fabricBeforeImg4 from '@/assets/images/fabric-before-4.jpeg';
import fabricAfterImg4 from '@/assets/images/fabric-after-4.jpeg';


// Import the BeforeAfterImage type
import { BeforeAfterImage } from '@/components/BeforeAfterGallery';

// Add FAQ interface
interface FAQ {
  question: string;
  answer: string;
}

export interface ServiceOption {
  category: string;
  options: string[];
  addons?: string[]; // Make addons configurable per service
  faqs: FAQ[]; // Add FAQs to service options
  // Add dropdown styling configuration
  dropdownStyles?: {
    bgColorOpen?: string;
    borderColorOpen?: string;
    textColorOpen?: string;
    iconColorOpen?: string;
  };
  // Add before/after images
  beforeAfterImages?: BeforeAfterImage[];
  // Add button color for before/after gallery
  beforeAfterButtonColor?: string;
}

// Add helper functions to extract title and price
export const extractTitle = (optionString: string): string => {
  // Extract just the title part (everything before the price, handles both single and range prices)
  const match = optionString.match(/(.*?)\s+RM[\d.]+(?:~[\d.]+)?$/);
  return match ? match[1].trim() : optionString;
};

export const extractPrice = (optionString: string): string => {
  // Extract just the price part (handles both single prices like "RM15" and range prices like "RM15~30")
  const match = optionString.match(/RM[\d.]+(?:~[\d.]+)?$/);
  return match ? match[0] : '';
};

import type { TFunction } from 'i18next';

// Factory function to get services with translations
export const getServices = (t: TFunction) => [
  {
    id: 'car-polish',
    title: t('serviceDetails.carPolish.title'),
    description: t('serviceDetails.carPolish.description'),
    backgroundImage: carDetailingBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-red-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-red-100'
  },
  {
    id: 'aircond',
    title: t('serviceDetails.aircond.title'),
    description: t('serviceDetails.aircond.description'),
    backgroundImage: aircondBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-blue-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-blue-100'
  },
  {
    id: 'fabric',
    title: t('serviceDetails.fabric.title'),
    description: t('serviceDetails.fabric.description'),
    backgroundImage: fabricBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-yellow-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-yellow-100'
  },
  {
    id: 'sanitise',
    title: t('serviceDetails.sanitise.title'),
    description: t('serviceDetails.sanitise.description'),
    backgroundImage: sanitiseBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-green-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-green-100'
  },
  {
    id: 'pest-control',
    title: t('serviceDetails.pestControl.title'),
    description: t('serviceDetails.pestControl.description'),
    backgroundImage: pestControlBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-brown-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-brown-100'
  }
] as const;

// Factory function to get service options with translations
export const getServiceOptions = (t: TFunction): Record<string, ServiceOption> => {
  const serviceKeyMap: Record<string, string> = {
    'car-polish': 'carPolish',
    'aircond': 'aircond',
    'fabric': 'fabric',
    'sanitise': 'sanitise',
    'pest-control': 'pestControl'
  };

  return {
    'car-polish': {
      category: t('serviceDetails.carPolish.category') || '',
      options: (t('serviceDetails.carPolish.options', { returnObjects: true }) as string[]) || [],
      addons: (t('serviceDetails.carPolish.addons', { returnObjects: true }) as string[]) || [],
      faqs: t(`faqs.${serviceKeyMap['car-polish']}`, { returnObjects: true }) as FAQ[],
      dropdownStyles: {
        bgColorOpen: 'bg-white-100',
        borderColorOpen: 'border-red-300',
        textColorOpen: 'text-red-600',
        iconColorOpen: 'text-red-600'
      },
      beforeAfterImages: [
        {
          id: 'car-polish-1',
          beforeImage: carBeforeImg1,
          afterImage: carAfterImg1,
          alt: 'Car polish before and after'
        },
        {
          id: 'car-polish-2',
          beforeImage: carBeforeImg2,
          afterImage: carAfterImg2,
          alt: 'Car polish before and after'
        },
        {
          id: 'car-polish-3',
          beforeImage: carBeforeImg3,
          afterImage: carAfterImg3,
          alt: 'Car polish before and after'
        },
        {
          id: 'car-polish-4',
          beforeImage: carBeforeImg4,
          afterImage: carAfterImg4,
          alt: 'Car polish before and after'
        }
      ],
      beforeAfterButtonColor: 'bg-trhine-red-100',
    },
    'aircond': {
      category: t('serviceDetails.aircond.category') || '',
      options: (t('serviceDetails.aircond.options', { returnObjects: true }) as string[]) || [],
      addons: (t('serviceDetails.aircond.addons', { returnObjects: true }) as string[]) || [],
      faqs: t(`faqs.${serviceKeyMap['aircond']}`, { returnObjects: true }) as FAQ[],
      dropdownStyles: {
        bgColorOpen: 'bg-white-100',
        borderColorOpen: 'border-blue-900',
        textColorOpen: 'text-blue-800',
        iconColorOpen: 'text-trhine-blue-600'
      },
      beforeAfterImages: [
        {
          id: 'aircond-1',
          beforeImage: aircondBeforeImg1,
          afterImage: aircondAfterImg1,
          alt: 'Aircond service before and after'
        },
        {
          id: 'aircond-2',
          beforeImage: aircondBeforeImg2,
          afterImage: aircondAfterImg2,
          alt: 'Aircond service before and after'
        },
        {
          id: 'aircond-3',
          beforeImage: aircondBeforeImg3,
          afterImage: aircondAfterImg3,
          alt: 'Aircond service before and after'
        },
        {
          id: 'aircond-4',
          beforeImage: aircondBeforeImg4,
          afterImage: aircondAfterImg4,
          alt: 'Aircond service before and after'
        }
      ],
      beforeAfterButtonColor: 'bg-trhine-blue-100',
    },
    'fabric': {
      category: t('serviceDetails.fabric.category') || '',
      options: (t('serviceDetails.fabric.options', { returnObjects: true }) as string[]) || [],
      addons: (t('serviceDetails.fabric.addons', { returnObjects: true }) as string[]) || [],
      faqs: t(`faqs.${serviceKeyMap['fabric']}`, { returnObjects: true }) as FAQ[],
      dropdownStyles: {
        bgColorOpen: 'bg-white-100',
        borderColorOpen: 'border-trhine-yellow-100',
        textColorOpen: 'text-yellow-800',
        iconColorOpen: 'text-trhine-yellow-600'
      },
      beforeAfterImages: [
        {
          id: 'fabric-1',
          beforeImage: fabricBeforeImg1,
          afterImage: fabricAfterImg1,
          alt: 'Fabric cleaning before and after'
        },
        {
          id: 'fabric-2',
          beforeImage: fabricBeforeImg2,
          afterImage: fabricAfterImg2,
          alt: 'Fabric cleaning before and after'
        },
        {
          id: 'fabric-3',
          beforeImage: fabricBeforeImg3,
          afterImage: fabricAfterImg3,
          alt: 'Fabric cleaning before and after'
        },
        {
          id: 'fabric-4',
          beforeImage: fabricBeforeImg4,
          afterImage: fabricAfterImg4,
          alt: 'Fabric cleaning before and after'
        }
      ],
      beforeAfterButtonColor: 'bg-trhine-yellow-100',
    },
    'sanitise': {
      category: t('serviceDetails.sanitise.category') || '',
      options: (t('serviceDetails.sanitise.options', { returnObjects: true }) as string[]) || [],
      addons: (t('serviceDetails.sanitise.addons', { returnObjects: true }) as string[]) || [],
      faqs: t(`faqs.${serviceKeyMap['sanitise']}`, { returnObjects: true }) as FAQ[],
      dropdownStyles: {
        bgColorOpen: 'bg-white-100',
        borderColorOpen: 'border-trhine-green-100',
        textColorOpen: 'text-green-600',
        iconColorOpen: 'text-trhine-green-600'
      },
      beforeAfterImages: [],
      beforeAfterButtonColor: 'bg-trhine-green-100',
    },
    'pest-control': {
      category: t('serviceDetails.pestControl.category') || '',
      options: (t('serviceDetails.pestControl.options', { returnObjects: true }) as string[]) || [],
      addons: (t('serviceDetails.pestControl.addons', { returnObjects: true }) as string[]) || [],
      faqs: t(`faqs.${serviceKeyMap['pest-control']}`, { returnObjects: true }) as FAQ[],
      dropdownStyles: {
        bgColorOpen: 'bg-white-100',
        borderColorOpen: 'border-trhine-brown-100',
        textColorOpen: 'text-trhine-brown-100',
        iconColorOpen: 'text-trhine-brown-600'
      },
      beforeAfterImages: [],
      beforeAfterButtonColor: 'bg-trhine-brown-100',
    }
  };
};

// Legacy export for backward compatibility (uses Malay as default)
export const services = [
  {
    id: 'car-polish',
    title: '`Polish` Kereta',
    description: 'Berikan kereta anda penampilan seperti baharu dengan perkhidmatan polish dan coating profesional kami.',
    backgroundImage: carDetailingBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-red-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-red-100'
  },
  {
    id: 'aircond',
    title: 'Servis Penghawa Dingin',
    description: 'Pastikan penghawa dingin anda berfungsi pada tahap optimum dengan perkhidmatan penyelenggaraan profesional kami.',
    backgroundImage: aircondBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-blue-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-blue-100'
  },
  {
    id: 'fabric',
    title: 'Pembersihan Fabrik',
    description: 'Pulihkan kesegaran dan kebersihan fabrik anda dengan perkhidmatan pembersihan deep cleaning profesional kami.',
    backgroundImage: fabricBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-yellow-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-yellow-100'
  },
  {
    id: 'sanitise',
    title: 'Pembasmian Kuman',
    description: 'Lindungi kesihatan keluarga dan pekerja anda dengan perkhidmatan sanitasi profesional kami.',
    backgroundImage: sanitiseBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-green-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-green-100'
  },
  {
    id: 'pest-control',
    title: 'Kawalan Serangga',
    description: 'Bebaskan rumah atau premis anda daripada gangguan makhluk perosak dengan perkhidmatan kawalan serangga profesional kami.',
    backgroundImage: pestControlBg,
    primaryButtonActive: true,
    secondaryButtonActive: false,
    buttonColor: 'bg-trhine-brown-100',
    bookingUrl: '',
    pricingUrl: '',
    footer_bg: 'bg-trhine-brown-100'
  }
] as const;

export type ServiceId = typeof services[number]['id']; 